import { useEffect, useState } from "react";

import { notify } from "../lib/notify";
import { useStore } from "../store";
import type { SubTask, Task } from "../types";
import { Button } from "./ui/button";
import { Modal } from "./ui/modal";

const clone = (t: Task): Task => JSON.parse(JSON.stringify(t));

/**
 * «Ролик не выйдет»: снять его с причиной.
 *
 * Было window.prompt, и «Отмена» в нём всё равно снимала ролик (`?? ""`
 * превращал отказ в пустую причину). Теперь — своё окно: без причины кнопки
 * нет, «Отмена» ничего не делает, а снятое можно вернуть «Отменить» в тосте —
 * запрос уходит, только когда тост догорел.
 */
export function CancelItemDialog({ task, item, open, onClose, onDone }: {
  task: Task;
  item: SubTask;
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
}) {
  const cancelItem = useStore((s) => s.cancelItem);
  const restore = useStore((s) => s.restore);
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (open) setReason("");
  }, [open]);
  const ok = reason.trim().length >= 3;

  const submit = () => {
    if (!ok) return;
    const snap = clone(task);
    cancelItem(task.id, item.id, reason.trim());
    onClose();
    onDone?.();
    notify(`«${item.title}» снято`, { icon: "info", undo: () => restore(snap) });
  };

  return (
    <Modal open={open} onClose={onClose} width={460} zIndex={60}>
      <form
        // Окно рисуется порталом, но клики всплывают по React-дереву — до строки, из которой его открыли.
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col gap-4 px-7 pb-6 pt-6 max-md:px-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="flex flex-col gap-1">
          <h2 className="text-[20px] font-semibold tracking-[-0.02em]">Ролик не выйдет?</h2>
          <p className="truncate text-sm text-muted-foreground">«{item.title}» — снимем его с публикации. Причина попадёт в историю.</p>
        </div>
        <textarea
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
          }}
          rows={3}
          maxLength={500}
          placeholder="Почему: инфоповод устарел, руководитель попросил убрать…"
          className="w-full resize-none rounded-[20px] border border-input bg-background px-[18px] py-3 text-sm leading-relaxed shadow-mxs outline-none placeholder:text-muted-foreground focus:ring-4 focus:ring-ring/20"
        />
        <div className="flex justify-end gap-2.5">
          <Button type="button" variant="outline" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={!ok} title={ok ? undefined : "Напишите причину"}>Снять ролик</Button>
        </div>
      </form>
    </Modal>
  );
}
